import { styled, useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { MapFilters, ScooterStatus } from '../../../ZonesPage/interface';
import { PlaybackScooter } from './types';
import { getScooterColor } from '../../../../helpers/scooter-markers-helper';
import ElectricScooterIcon from '@mui/icons-material/ElectricScooter';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import BuildIcon from '@mui/icons-material/Build';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import Battery20Icon from '@mui/icons-material/Battery20';

const PREFIX = 'PlaybackScooterCounters';

const classes = {
  container: `${PREFIX}-container`
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 6,
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 8
    },
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(5, 1fr)'
    }
  }
}));

const hexToRgba = (hex: string, alpha: number = 1): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const StyledCounterCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor'
})<{ bgColor: string }>(({ bgColor }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 10px',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: hexToRgba(bgColor, 0.12),
  border: 'none',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
  minHeight: 50,
  
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.12)',
  }
}));

const LeftColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8
});

const IconCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor'
})<{ bgColor: string }>(({ bgColor }) => ({
  width: 28,
  height: 28,
  borderRadius: '6px',
  backgroundColor: hexToRgba(bgColor, 0.18),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    width: 16,
    height: 16,
    color: bgColor
  }
}));

const CountText = styled(Box)<{ textColor: string }>(({ textColor }) => ({
  fontSize: 20,
  fontWeight: 700,
  color: textColor,
  lineHeight: 1
}));

const LabelText = styled('span')(({ theme }) => ({
  fontSize: 10,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  textAlign: 'left'
}));

interface CounterItemProps {
  title: string;
  count: number;
  color: string;
  onPress?: () => void;
  icon: React.ReactNode;
}

const CounterItem = ({ title, count, color, onPress, icon }: CounterItemProps) => {
  return (
    <StyledCounterCard 
      bgColor={color}
      onClick={() => onPress?.()}
    >
      <LeftColumn>
        <IconCircle bgColor={color}>
          {icon}
        </IconCircle>
        <LabelText>{title}</LabelText>
      </LeftColumn>
      <CountText textColor={color}>{count}</CountText>
    </StyledCounterCard>
  );
};

interface Props {
  scooters: PlaybackScooter[];
  mapFilters?: MapFilters;
  onFilterSet?: (val: MapFilters) => void;
}

const PlaybackScooterCounters = ({ scooters, mapFilters, onFilterSet }: Props) => {
  const theme = useTheme();

  const stats = useMemo(() => {
    const total = scooters.length;
    const free = scooters.filter(s => {
      const status = s.status?.toUpperCase() || 'AVAILABLE';
      return !['RESERVED', 'RIDE'].includes(status);
    }).length;
    const onContract = scooters.filter(s => {
      const status = s.status?.toUpperCase() || 'AVAILABLE';
      return status === 'RIDE' || status === 'RESERVED';
    }).length;
    const transporting = scooters.filter(s => s.status === ScooterStatus.TRANSPORTING).length;
    const charging = scooters.filter(s => s.status === ScooterStatus.CHARGING).length;
    const maintenance = scooters.filter(s => s.status === ScooterStatus.MAINTENANCE).length;
    const offline = scooters.filter(s => !s.connected).length;
    const unlocked = scooters.filter(s => !s.lock).length;
    const broken = scooters.filter(s => s.status === ScooterStatus.BROKEN_IN_CITY).length;
    const lowBattery = scooters.filter(s => s.status === ScooterStatus.LOW_BATTERY).length;

    return {
      total,
      free,
      onContract,
      transporting,
      charging,
      maintenance,
      offline,
      unlocked,
      broken,
      lowBattery
    };
  }, [scooters]);

  const onUpdateFilters = (value: MapFilters) => {
    const filters = { ...mapFilters };

    if (
      Object.keys(value).includes('free') ||
      Object.keys(value).includes('offline')
    ) {
      delete filters?.status;
      delete value?.status;
    }

    if (Object.keys(value).includes('status')) {
      delete filters?.free;
      delete value?.free;
      delete filters?.offline;
      delete value?.offline;
    }

    if (value?.free === filters?.free) {
      delete filters?.free;
      delete value?.free;
    }

    if (value?.offline === filters?.offline) {
      delete filters?.offline;
      delete value?.offline;
    }

    const newFilters = {
      ...filters,
      ...value
    };
    onFilterSet?.(newFilters);
  };

  const onSetStatus = (status: ScooterStatus) => {
    let arr = mapFilters?.status?.split(',') ?? [];

    let index = arr.indexOf(status);
    if (index > -1) {
      arr.splice(index, 1);
    } else {
      arr.push(status);
    }

    let newStatus = arr.join(',');

    onUpdateFilters({ status: newStatus });
  };

  const filters = mapFilters ?? {};
  
  const isFree = useMemo(
    () =>
      filters.free ||
      filters.free === 'all',
    [filters]
  );
  const isInRent = useMemo(
    () =>
      (Object.keys(filters).includes('free') &&
        Boolean(filters.free) === false) ||
      filters.free === 'all',
    [filters]
  );

  return (
    <Root className={classes.container}>
      <CounterItem
        onPress={() => onFilterSet?.({})}
        title='Всі'
        count={stats.total}
        color={getScooterColor('all')}
        icon={<ElectricScooterIcon />}
      />
      <CounterItem
        onPress={() =>
          onUpdateFilters(
            isInRent && mapFilters?.free !== 'all'
              ? { free: 'all' }
              : { free: true }
          )
        }
        title='Вільні'
        count={stats.free}
        color={getScooterColor('free')}
        icon={<CheckCircleIcon />}
      />
      <CounterItem
        onPress={() =>
          onUpdateFilters(
            isFree && mapFilters?.free !== 'all'
              ? { free: 'all' }
              : { free: false }
          )
        }
        title='У поїздці'
        count={stats.onContract}
        color={getScooterColor('inRent')}
        icon={<DirectionsBikeIcon />}
      />
      <CounterItem
        onPress={() => onUpdateFilters({ unlocked: true })}
        title='Розблокований'
        count={stats.unlocked}
        color={getScooterColor('unlocked')}
        icon={<LockOpenIcon />}
      />
      <CounterItem
        onPress={() => onSetStatus?.(ScooterStatus.TRANSPORTING)}
        title='Перевезення'
        count={stats.transporting}
        color={getScooterColor(ScooterStatus.TRANSPORTING)}
        icon={<LocalShippingIcon />}
      />
      <CounterItem
        onPress={() => onSetStatus?.(ScooterStatus.CHARGING)}
        title='На зарядці'
        count={stats.charging}
        color={getScooterColor(ScooterStatus.CHARGING)}
        icon={<BatteryChargingFullIcon />}
      />
      <CounterItem
        onPress={() => onSetStatus?.(ScooterStatus.MAINTENANCE)}
        title='В ремонті'
        count={stats.maintenance}
        color={getScooterColor(ScooterStatus.MAINTENANCE)}
        icon={<BuildIcon />}
      />
      <CounterItem
        onPress={() => onUpdateFilters({ offline: true })}
        title='Оффлайн'
        count={stats.offline}
        color={getScooterColor('offline')}
        icon={<WifiOffIcon />}
      />
      <CounterItem
        onPress={() => onSetStatus?.(ScooterStatus.BROKEN_IN_CITY)}
        title='Зламаний в місті'
        count={stats.broken}
        color={getScooterColor(ScooterStatus.BROKEN_IN_CITY)}
        icon={<ReportProblemIcon />}
      />
      <CounterItem
        onPress={() => onSetStatus?.(ScooterStatus.LOW_BATTERY)}
        title='Розряджений'
        count={stats.lowBattery}
        color={theme.palette.mode === 'light' ? '#000000' : getScooterColor('discharged')}
        icon={<Battery20Icon />}
      />
    </Root>
  );
};

export default PlaybackScooterCounters;
